//* Vue
import type { SetupContext } from "vue";
//* 组件库
import { $loading, $message, type UiFormItem, type UiForm, type UiTypes, utility } from "various-ui";
//* 私有组件
import type ModuleCodonContrast from "../../codon-contrast/index.vue";
import type ModulePreview from "../components/preview/index.vue";
//* 组件属性
import type { ModuleFormData, ModuleProps, ModuleEmits } from "./types";
import { utils } from "./utils";

export const useComposable = (define: ModuleProps, emits: SetupContext<typeof ModuleEmits>["emit"]) => {
    //* 插件
    const i18n = useI18n();
    //* 静态数据
    const vars = {
        datas: <{ [name: number]: ModuleFormData | undefined }>{},
        modes: [
            { label: i18n.t("FastPaste"), value: "Paste" },
            { label: i18n.t("InputMutation"), value: "Variant" },
            { label: i18n.t("InputProtein"), value: "Protein" },
            { label: i18n.t("InputTranscript"), value: "Transcript" },
        ],
    };

    //* 响应式数据
    const data = reactive<ModuleFormData[]>([utils.data()]);
    const refs = {
        sync: ref<boolean>(false), //* 是否同步进行相关预测
    };

    //* 表单校验规则
    const rules: { [name: string]: UiTypes.verifyRule[] } = {
        site: [
            {
                trigger: "verify",
                verify: (response) => {
                    //* 转录本坐标检测
                    if (response.mode == "Transcript") {
                        //* 数据初始化
                        const end = response.data.range?.end || 0;
                        const start = response.data.range?.start || 0;
                        //* 统计序列长度
                        const size = response.sequence?.length || 0;

                        //* 坐标是否添加完整检测
                        if (!response.data.coordinate.start || !response.data.coordinate.end)
                            return { verify: false, message: i18n.t("Placeholder.TranscriptNotNull") };
                        //* 坐标超出基因范围检测
                        if (start < 1 || start > size || end < 1 || end > size)
                            return { verify: false, message: i18n.t("Placeholder.MutationExceedRange") };
                        //* 起始坐标超过终止坐标检测
                        if (start > end) {
                            return { verify: false, message: i18n.t("Placeholder.StartExceedEnd") };
                        }
                    }

                    //* 蛋白坐标检测
                    if (response.mode == "Protein") {
                        if (!response.data.site) {
                            return { verify: false, message: i18n.t("Placeholder.ProteinNotNull") };
                        }
                    }

                    //* 突变坐标检测
                    if (response.mode == "Variant" || response.mode == "Paste") {
                        if (!response.data.keyword) {
                            return { verify: false, message: i18n.t("FormatError") };
                        }
                    }

                    //* 数据处理
                    if (response.mode == "Paste" && !response.id) return { verify: false, message: i18n.t("MutationsInputsError1") };

                    //* 坐标长度检测
                    if (!response.data.range) return { verify: false, message: i18n.t("CoordinatesNotFound") };
                    else if (response.data.range.end - response.data.range.start + 1 > 50) {
                        return { verify: false, message: i18n.t("MutationRangeLT50") };
                    }

                    return { verify: true, message: "" };
                },
            },
        ],

        name: [
            {
                trigger: "verify",
                verify: (response) => {
                    if (!response.id) return { verify: false, message: i18n.t("PleaseSelectGene") };
                    else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],
    };

    //* 节点
    const nodes = {
        forms: ref<InstanceType<typeof UiForm>[]>([]),
        sites: ref<InstanceType<typeof UiFormItem>[]>([]),
        views: ref<InstanceType<typeof ModulePreview>[]>([]),
        contrast: ref<InstanceType<typeof ModuleCodonContrast>>(),
    };

    //* 函数列表
    const methods = {
        //* 突变后碱基数据处理
        alt: (key: number) => {
            //* 数据格式更新
            nextTick(() => {
                //* 数据更新
                data[key].alt = utils.codon(data[key].alt);
                //* 触发可视化图形重绘
                nodes.views.value[key]?.render(data[key]);
            });
        },

        //* 预测模式切换
        mode: (key: number) => {
            //* 缓存数据取出
            const cache = utility.cloneDeep(vars.datas[key]);
            //* 数据缓存
            vars.datas[key] = utility.cloneDeep(data[key]);
            //* 初始化数据
            if (cache && ((cache.id == data[key].id && cache.transcript == data[key].transcript) || !data[key].id)) {
                data[key] = utils.data(Object.assign(cache, { mode: data[key].mode, visible: data[key].visible }), {
                    clear: data[key].mode == "Paste",
                });
            } else {
                data[key] = utils.data(data[key], { clear: data[key].mode == "Paste" });
            }

            //* 触发可视化图形重绘
            nodes.views.value[key]?.render(data[key]);
            //* 对应模式需生成新的对照表
            switch (data[key].mode) {
                case "Protein": {
                    return (data[key].data.contrast = utils.codon_contrast(data[key]));
                }

                case "Transcript": {
                    return (data[key].data.contrast = utils.transcript_contrast(data[key]));
                }
            }
        },

        //* 基因选择
        gene: (key: number, result: any, callback?: () => void) => {
            console.log("基因信息", result);
            //* 开启加载器
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
            api.frontend("/api/gene/mrnas-info", {
                data: { id: result.id },
                error: () => {
                    $loading.close();
                    utils.clear(data, key);
                },
                success: (response) => {
                    $loading.close();
                    //* 数据更新
                    data[key].transcripts = utils.transcript(response?.data);
                    data[key].transcript = data[key].transcripts?.[0]?.value || "";
                    data[key].sequence = response?.sequence || "";
                    data[key].species = response?.taxonomy_id || "";
                    data[key].name = response?.name || "";
                    data[key].id = result.id;
                    //* 触发表单校验（用于重置表单校验状态）
                    nodes.forms.value[key]?.clear();
                    nodes.forms.value[key]?.validator(() => {}, ["name"]);
                    //* 选择第一个转录本
                    methods.transcript(key);
                    //* 回调函数
                    callback?.();
                },
            });
        },

        //* 删除
        remove: (key: number) => {
            //* 数据删除
            data.splice(key, 1);
            //* 遍历强制重绘可视化图形
            nextTick(() => {
                data.forEach((value, index) => nodes.views.value[index]?.render(value, { update: true }));
            });
        },

        //* 添加
        append: () => {
            //* 数据添加
            const index = data.length;
            const response = data.slice(-1)[0];
            if (response.mode == "Paste") data.push(utils.data(response, { clear: true }));
            else {
                data.push(utils.data(response));
                nextTick(() => {
                    //* 可视化模型绘制
                    if (response.visible) nodes.views.value[index]?.render(data[index]);
                    //* 对应模式需生成新的对照表
                    switch (data[index].mode) {
                        case "Protein": {
                            return (data[index].data.contrast = utils.codon_contrast(data[index]));
                        }

                        case "Transcript": {
                            return (data[index].data.contrast = utils.transcript_contrast(data[index]));
                        }
                    }
                });
            }
        },

        //* 图表开关
        visible: (key: number) => {
            data[key].visible = !data[key].visible;
            if (data[key].visible) {
                //* 触发可视化图形重绘
                nodes.views.value[key]?.render(data[key]);
            }
        },

        //* 突变解析
        analysis: (key: number) => {
            //* 检测类型是否合法
            if (!data[key] || data[key].mode == "Protein" || data[key].mode == "Transcript") return;
            //* 解析内容空值校验
            const option: any = { keyword: data[key].data.keyword?.trim() || "", source: define.mode };
            if (!option.keyword) {
                utils.analysis(data, key);
                nodes.sites.value[key]?.validator();
                nextTick(() => {
                    nodes.views.value[key]?.render(data[key]);
                });

                //* 退出解析
                return;
            }

            //* Variant模式下需添加id与转录本
            if (data[key].mode != "Paste") {
                option.id = data[key].id;
                option.transcript = data[key].transcript;
            }

            //* 开启加载器
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
            //* 检测突变信息是否合法
            api.frontend("/api/mutations/analyze2", {
                data: option,
                hidden: true,
                method: "POST",
                error: (error) => {
                    //* 数据处理
                    utils.analysis(data, key);
                    //* 消息提示
                    nodes.sites.value[key]?.show(error.message, "error");
                    //* 突变解析失败时需要重置可视化图形
                    if (data[key].mode == "Variant") {
                        nodes.views.value[key]?.render(data[key]);
                    }
                },
                finally: () => $loading.close(),
                success: (response) => {
                    console.log("解析结果", response);
                    //* 数据处理
                    if (data[key].mode != "Paste" || (data[key].mode == "Paste" && response.id)) utils.analysis(data, key, response);
                    else {
                        //* 数据处理
                        utils.analysis(data, key);
                        //* 消息提示
                        nodes.sites.value[key]?.show(i18n.t("MutationsInputsError1"), "error");
                        //* 停止向下执行
                        return;
                    }

                    //* 数据校验
                    nextTick(() => {
                        nodes.forms.value[key]?.validator((result) => {
                            //* 校验失败则清空预测坐标
                            if (!result) data[key].data.range = undefined;
                            //* 坐标匹配失败警告
                            else if (response?.msg) nodes.sites.value[key]?.show(response.msg, "warning");
                            else {
                                //* 成功提示
                                $message.success(i18n.t("DataResolveSuccess"));
                            }

                            //* 触发可视化图形重绘
                            nodes.views.value[key]?.render(data[key]);
                        });
                    });
                },
            });
        },

        //* 转录本切换
        transcript: (key: number) => {
            //* 检测类型是否合法
            if (!data[key]) return;
            //* 公共模式处理
            if (data[key].mode != "Paste") {
                data[key].data.range = undefined;
                data[key].ref = "";
                data[key].alt = "";
                //* 触发可视化图形重绘
                nodes.views.value[key]?.render(data[key]);
            }

            //* 转录本模式处理
            if (data[key].mode == "Transcript") {
                //* 生成新的转录本对照表
                data[key].data.contrast = utils.transcript_contrast(data[key]);
                //* 数据重置
                data[key].data.coordinate = {};
                //* 停止向下执行
                return;
            }

            //* 蛋白模式处理
            if (data[key].mode == "Protein") {
                //* 生成新的蛋白对照表
                data[key].data.contrast = utils.codon_contrast(data[key]);
                //* 数据重置
                data[key].data.site = "";
                //* 停止向下执行
                return;
            }

            //* 解析模式处理
            if (data[key].mode == "Variant") {
                //* 数据重置
                data[key].data.keyword = "";
                //* 停止向下执行
                return;
            }

            //* 粘贴模式处理
            if (data[key].mode == "Paste") {
                //* 开启加载器
                $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
                //* 检测突变信息是否合法
                api.frontend("/api/mutations/analyze2", {
                    data: { id: data[key].id, keyword: data[key].data.keyword.trim(), transcript: data[key].transcript, source: define.mode },
                    hidden: true,
                    method: "POST",
                    error: (error) => {
                        //* 数据更新
                        data[key].alt = "";
                        data[key].ref = "";
                        data[key].data.range = undefined;
                        //* 触发可视化图形重绘
                        nodes.views.value[key]?.render(data[key]);
                        //* 消息提示
                        nodes.sites.value[key]?.show(error.message, "error");
                    },
                    finally: () => $loading.close(),
                    success: (response) => {
                        //* 数据更新
                        data[key].alt = utils.codon(response?.alt || "");
                        data[key].ref = response?.ref || "";
                        data[key].data.range = { end: response?.range?.[1], start: response?.range?.[0] };
                        //* 数据校验
                        nextTick(() => {
                            nodes.forms.value[key]?.validator((result) => {
                                //* 校验失败则清空预测坐标
                                if (!result) data[key].data.range = undefined;
                                //* 触发可视化图形重绘
                                nodes.views.value[key]?.render(data[key]);
                            });
                        });
                    },
                });
            }
        },

        //* 蛋白坐标
        protein_site: (key: number) => {
            if (data[key].mode != "Protein") return;
            else {
                //* 数据获取
                const result = data[key].data.contrast[data[key].data.site];
                //* 数据更新
                data[key].ref = result?.codon || "";
                if (!result) data[key].data.range = undefined;
                else {
                    data[key].data.range = { end: result.end, start: result.start };
                }

                //* 坐标校验
                nodes.forms.value[key]?.validator(
                    (result) => {
                        //* 数据更新
                        if (!result) data[key].data.range = undefined;
                        nextTick(() => {
                            //* 触发可视化图形重绘
                            nodes.views.value[key]?.render(data[key]);
                        });
                    },
                    ["site"]
                );
            }
        },

        //* 转录本坐标
        transcript_site: (key: number) => {
            if (data[key].mode != "Transcript") return;
            else {
                //* 索引初始化
                const index = { start: data[key].data.coordinate?.start || 0, end: data[key].data.coordinate?.end || 0 };
                //* 数据初始化
                const end = Number(data[key].data.contrast?.[index.end]?.site) + (Number(data[key].data.coordinate?.["end-steps"]) || 0);
                const start = Number(data[key].data.contrast?.[index.start]?.site) + (Number(data[key].data.coordinate?.["start-steps"]) || 0);
                //* 数据更新
                data[key].ref = data[key].sequence.slice(start - 1, end);
                if (start > 0 && end > 0) data[key].data.range = { end: end, start: start };
                else {
                    data[key].data.range = undefined;
                }

                //* 坐标校验
                nodes.forms.value[key]?.validator(
                    (result) => {
                        //* 数据更新
                        if (!result) data[key].data.range = undefined;
                        nextTick(() => {
                            //* 触发可视化图形重绘
                            nodes.views.value[key]?.render(data[key]);
                        });
                    },
                    ["site"]
                );
            }
        },

        //* 初始化函数
        init: (response: { id: string; nucleotide: string; transcript: string }) => {
            //* 数据初始化
            data[0].mode = "Variant";
            methods.mode(0);
            methods.gene(0, response, () => {
                //* 转录本选择
                data[0].transcript = data[0].transcripts.find((v) => v.value == response.transcript)?.value || data[0].transcript;
                if (data[0].transcript) {
                    methods.transcript(0);
                }

                //* 突变解析
                if (data[0].mode == "Variant") {
                    data[0].data.keyword = response.nucleotide;
                    if (data[0].data.keyword) {
                        methods.analysis(0);
                    }
                }
            });
        },

        //* 提交函数
        submit: () => {
            //* 表单数据获取
            const result = { status: 200, data: <ModuleFormData[]>[] };
            //* 递归校验函数
            const verify = async (index: number) => {
                if (!nodes.forms.value[index]) {
                    if (result.status == 200) emits("submit", { datas: result.data, syns: refs.sync.value });
                } else {
                    //* 表单校验
                    try {
                        await nodes.forms.value[index].validator((validator) => {
                            if (!validator) result.status = 500;
                            else {
                                result.data.push(data[index]);
                            }
                        });
                    } catch {
                        result.status = 500;
                    }

                    //* 递归
                    verify(index + 1);
                }
            };

            //* 第一次递归
            verify(0);
        },
    };

    return { vars, refs, data, rules, nodes, methods };
};
